import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, Lightbulb, CheckCircle2, Loader2, ExternalLink } from 'lucide-react'
import { Dot, SectionCard, NotAvailable, ErrorBanner, KeyValueRow, Tag } from '../ui'
import { ISSUE_TAB_LABEL, formatRelative } from '../../lib/support'
import { ActivityItem } from './ActivityTab'

const IssueCard = ({ issue, onTab }) => (
  <div className="card grid grid-cols-[10px_minmax(0,1fr)] gap-3.5 p-4">
    <Dot tone={issue.tone} size={10} className="mt-1.5" />
    <div className="flex flex-col gap-1.5">
      <div className="flex flex-wrap items-baseline gap-2.5">
        <span className="text-[15px] font-bold">{issue.title}</span>
        <span className="text-[13px] text-admin-600">{issue.detail}</span>
      </div>
      <div className="flex gap-2 rounded-md bg-admin-50 px-3 py-2.5 text-[13px]">
        <Lightbulb className="mt-0.5 h-3.5 w-3.5 flex-none text-primary-700" />
        <span>{issue.fix}</span>
      </div>
      {issue.tab && ISSUE_TAB_LABEL[issue.tab] && (
        <button type="button" className="btn btn-ghost self-start" onClick={() => onTab(issue.tab)}>
          {ISSUE_TAB_LABEL[issue.tab]} <ArrowRight className="h-4 w-4" />
        </button>
      )}
    </div>
  </div>
)

const NotesCard = ({ notes, onAdd }) => {
  const [draft, setDraft] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const submit = async () => {
    const text = draft.trim()
    if (!text) return
    setSaving(true)
    setError('')
    try {
      await onAdd(text)
      setDraft('')
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message || 'Não foi possível salvar a nota')
    } finally {
      setSaving(false)
    }
  }

  return (
    <SectionCard title="Notas internas" aside={<span className="text-xs text-admin-500">Visível apenas para admins</span>}>
      {notes.unavailable ? (
        <NotAvailable>As notas internas dependem do endpoint de notas do estúdio, que ainda não foi implantado.</NotAvailable>
      ) : (
        <>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Adicione contexto para a próxima pessoa — o que foi tentado, o que o cliente disse…"
            aria-label="Nova nota interna"
            className="input min-h-[72px] bg-admin-50"
          />
          {error && <ErrorBanner>{error}</ErrorBanner>}
          <button type="button" className="btn btn-primary self-end" onClick={submit} disabled={saving || !draft.trim()}>
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Adicionar nota
          </button>
          {(notes.list || []).map((n) => (
            <div key={n.id} className="flex flex-col gap-0.5 rounded-md bg-admin-50 px-3 py-2.5">
              <span className="text-xs text-admin-500">
                {n.author_email} · {formatRelative(n.created_at)}
              </span>
              <span className="whitespace-pre-wrap text-[13px]">{n.text}</span>
            </div>
          ))}
        </>
      )}
    </SectionCard>
  )
}

// Signup contact + "same person" studios, from the CRM mirror's identity rules
// (vs-main-api services/crm_mirror.py): owner/billing email or a +55-insensitive phone.
// Staff get the phone masked by the API; the CRM link is owner-only.
const ContactCard = ({ tenant }) => {
  const related = tenant?.related_tenants || []
  return (
    <SectionCard
      title="Contato e CRM"
      aside={
        tenant?.crm_client_url && (
          <a href={tenant.crm_client_url} target="_blank" rel="noopener noreferrer" className="btn btn-ghost">
            Abrir no CRM <ExternalLink className="h-4 w-4" />
          </a>
        )
      }
    >
      <KeyValueRow label="E-mail de cobrança">{tenant?.billing_email || '—'}</KeyValueRow>
      <KeyValueRow label="Telefone do cadastro">{tenant?.contact_phone || '—'}</KeyValueRow>
      <div className="mt-1 flex flex-col gap-1.5">
        <span className="text-xs font-semibold text-admin-600">Outros estúdios deste contato</span>
        {related.length === 0 ? (
          <p className="text-[13px] text-admin-600">Nenhum outro estúdio com o mesmo e-mail ou telefone.</p>
        ) : (
          related.map((r) => (
            <Link
              key={r.id}
              to={`/tenants/${r.id}`}
              className="flex items-center justify-between gap-2 rounded-md bg-admin-50 px-3 py-2 text-[13px] hover:bg-admin-100"
            >
              <span className="min-w-0 truncate">
                {r.name} <span className="text-admin-500">· {r.slug}</span>
              </span>
              <Tag tone={r.is_active ? 'ok' : 'muted'}>{r.is_active ? 'Ativo' : 'Inativo'}</Tag>
            </Link>
          ))
        )}
      </div>
    </SectionCard>
  )
}

export const OverviewTab = ({ view, onTab, notes, onAddNote, activity }) => (
  <div className="flex flex-col gap-4">
    <section className="flex flex-col gap-2">
      <h4 className="text-sm">Pendências em aberto</h4>
      {view.issues.length === 0 ? (
        <div className="flex items-center gap-2.5 rounded-lg bg-green-50 p-4 text-green-800">
          <CheckCircle2 className="h-4 w-4 flex-none" />
          Todas as verificações passaram. Se o cliente relatar um problema, comece por Acesso ou WhatsApp.
        </div>
      ) : (
        view.issues.map((issue) => <IssueCard key={issue.title} issue={issue} onTab={onTab} />)
      )}
    </section>

    <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,340px),1fr))] items-start gap-4">
      <NotesCard notes={notes} onAdd={onAddNote} />
      <ContactCard tenant={view.model?.raw} />
      <SectionCard
        title="Atividade recente"
        aside={
          <button type="button" className="btn btn-ghost" onClick={() => onTab('activity')}>
            Registro completo <ArrowRight className="h-4 w-4" />
          </button>
        }
      >
        {activity.events.length === 0 ? (
          <p className="text-[13px] text-admin-600">Nenhuma atividade registrada ainda.</p>
        ) : (
          activity.events.slice(0, 5).map((e, i) => <ActivityItem key={i} event={e} compact />)
        )}
      </SectionCard>
    </div>
  </div>
)
